/**
 *  Copyright AlpineReplay Inc, 2021. All rights reserved.
 *  Authors: Brian Bal
 */
import React, { useState, useEffect, useMemo } from "react"
import { useSelector, useDispatch } from "react-redux"
import {
    Label,
    Button,
    ContentSection,
    FooterSection,
    Row,
    Input,
    useToken,
    getProperty,
    StepController,
    Step,
} from "trux"
import {
    useAddPhoneNumberMutation,
    useUserRoleQuery,
} from "../../generated/graphql"

export const PhoneForm = ({ onDone, teamName }) => {
    const token = useToken()
    const [phoneNumber, setPhoneNumber] = useState("")
    const [phoneError, setPhoneError] = useState(null)
    const [step, setStep] = useState("loading")
    const [roles, setRoles] = useState(null)
    const [askForPhone, setAskForPhone] = useState(false)

    const [
        addPhoneNumberMutation,
        {
            data: addNumberData,
            loading: addNubmerLoading,
            error: addNumberError,
        },
    ] = useAddPhoneNumberMutation()

    // handle mutation response
    useEffect(() => {
        let success = getProperty("addPhoneNumber.success", addNumberData, null)
        if (success === true) {
            setStep("confirm")
        } else if (success === false) {
            setPhoneError(
                getProperty(".addPhoneNumber.error", addNumberData, null)
            )
        }
    }, [addNumberData])

    useEffect(() => {
        if (step === "loading") {
            if (roles && roles.length > 0) {
                setStep(askForPhone ? "phone" : "no-phone")
            }
        }
    }, [askForPhone, roles])

    const { data: roleData } = useUserRoleQuery({
        variables: {
            token: token,
        },
        skip: !token || !token.user_id,
        onCompleted: (data) => {
            console.log("roles", data.userRole)
            let askForPhone = false
            for (let role of data.userRole) {
                switch (role) {
                    case "parent":
                    case "manager":
                    case "coach":
                    case "member":
                        askForPhone = true
                        break
                    default:
                        // nothing to do
                        break
                }
            }
            console.log("askForPhone", askForPhone)
            setAskForPhone(askForPhone)
            setRoles(data.userRole)
        },
    })

    const onSubmit = () => {
        // player, not asking for phone number
        if (!askForPhone) {
            onDone()
            return
        }

        // asking for phone number
        let cleanNumber = cleanPhoneNumber(phoneNumber)
        if (cleanNumber && !cleanNumber.startsWith("+")) {
            cleanNumber = "+1" + cleanNumber
        }
        const isValid = validatePhoneNumber(cleanNumber)
        if (isValid) {
            setPhoneError(null)
            addPhoneNumberMutation({
                variables: {
                    token,
                    phoneNumber: cleanNumber,
                },
            })
        } else {
            if (!cleanNumber) {
                onDone()
                return
            } else {
                setPhoneError("Invalid phone number")
            }
        }
    }

    const cleanPhoneNumber = (phoneNumber) => {
        let num = phoneNumber.replace(/[^\d+]/g, "")
        return num
    }

    const validatePhoneNumber = (phoneNumber) => {
        let num = cleanPhoneNumber(phoneNumber)
        // use regex to validate phone number
        const regex = /^\+\d{11,20}/
        if (!regex.test(num)) {
            return false
        }
        return true
    }

    return (
        <StepController step={step}>
            <Step name="loading">
                <ContentSection>
                    <Label
                        center
                        type={Label.Type.Body}
                        bold
                        content="Loading team, just a second ..."
                        marginBottom="5"
                        marginTop="5"
                    />
                </ContentSection>
            </Step>
            <Step name="no-phone">
                <ContentSection>
                    <Label
                        center
                        type={Label.Type.Body}
                        bold
                        content={`You are now on ${teamName} 🎉`}
                        marginBottom="5"
                        marginTop="5"
                    />
                </ContentSection>
                <FooterSection>
                    <Row center span="12">
                        <Button
                            trkAction="OK Pressed"
                            content="OK"
                            primary
                            onClick={onSubmit}
                            span="12"
                        />
                    </Row>
                </FooterSection>
            </Step>
            <Step name="phone">
                <ContentSection>
                    <Label
                        center
                        type={Label.Type.Body}
                        bold
                        content={`You are now on ${teamName} 🎉`}
                        marginBottom="5"
                        marginTop="5"
                    />
                    <Input
                        marginTop="8"
                        label="Your Phone Number"
                        value={phoneNumber}
                        onChange={setPhoneNumber}
                        error={phoneError}
                        description={
                            <>
                                Used to generate easy sign-in codes, links to
                                games results and faster support (in the
                                future). We won't spam you
                            </>
                        }
                    />
                </ContentSection>
                <FooterSection>
                    <Row center span="12">
                        <Button
                            trkAction="OK Pressed"
                            content="OK"
                            primary
                            onClick={onSubmit}
                            span="12"
                        />
                    </Row>
                </FooterSection>
            </Step>
            <Step name="confirm">
                <ContentSection>
                    <Label
                        center
                        type={Label.Type.Body}
                        bold
                        content="Thank You!"
                        marginBottom="8"
                        marginTop="8"
                    />
                </ContentSection>
                <FooterSection>
                    <Row center span="12">
                        <Button
                            trkAction="OK Pressed"
                            content="Done"
                            primary
                            onClick={onDone}
                            span="12"
                        />
                    </Row>
                </FooterSection>
            </Step>
        </StepController>
        /*
        <>
            <ContentSection>
                {roles && roles.length > 0 && (
                    <Label
                        center
                        type={Label.Type.Heading4}
                        content={`You are now on ${teamName}!`}
                        marginBottom="4"
                    />
                )}
                {(!roles || roles.length == 0) && (
                    <Label
                        center
                        type={Label.Type.Heading4}
                        content="Loading team, just a second ..."
                        marginBottom="8"
                    />
                )}
{askForPhone && (
            <Input
                marginTop="8"
                label="Phone Number"
                value={phoneNumber}
                onChange={setPhoneNumber}
                error={phoneError}
                description={
                    <>
                        Used for easy log in codes, links to game
                        results, <br />
                        and fast support &#40;in the future&#41;. We
                        won't spam you.
                    </>
                }
            />
        )}
            </ContentSection>
            {roles && roles.length > 0 && (
                <FooterSection>
                    <Row center span="12">
                        <Button
                            trkAction="OK Pressed"
                            content="OK"
                            primary
                            onClick={onSubmit}
                        />
                    </Row>
                </FooterSection>
            )}
        </>
        */
    )
}
